<template>
  <div class="player">
    <div class="player-controls">
      <div class="play">
        <img
          :src="imagePlayPauseSource"
          alt="Play"
          @click.prevent="playing = !playing"
          :title="playing ? 'Pause' : 'Play'"
          class="control-button"
        />
      </div>
      <div class="player-time">
        <div class="player-time-current">
          {{ convertTimeHHMMSS(currentSeconds) }}
        </div>
        <div class="seek">
          <div class="player-timeline">
            <div :style="progressStyle" class="player-progress"></div>
            <div v-on:click="seek" class="player-seeker" title="Seek"></div>
          </div>
        </div>
        <div class="player-time-total" v-if="currentSeconds > 0">
          -{{ convertTimeHHMMSS(durationLeft) }}
        </div>
        <div class="player-time-total" v-else>
          -{{ convertTimeHHMMSS(durationSeconds) }}
        </div>
      </div>
      <div id="volume">
        <img
          :src="imageSoundOnSoundOffSource"
          alt="Volume"
          class="control-button"
          @click.prevent="mute"
          :title="volumeTitle"
        />
      </div>
    </div>
    <audio
      ref="audioRef"
      :src="url"
      @timeupdate="update"
      @loadeddata="load"
      @pause="playing = false"
      @play="playing = true"
      preload="auto"
      style="display: none"
    ></audio>
  </div>
</template>
<script>
import { computed, watch, ref } from "vue";

export default {
  name: "AppAudio",
  props: {
    autoPlay: {
      type: Boolean,
      default: false,
    },
    url: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const currentSeconds = ref(0);
    const durationSeconds = ref(0);
    const durationLeft = ref(0);
    const loaded = ref(false);
    const playing = ref(false);
    const previousVolume = ref(35);
    const showVolume = ref(false);
    const volume = ref(100);
    const audioRef = ref("audio");
    const imagePlayPauseSource = computed(() =>
      playing.value
        ? require(`@/Common/Icons/PauseButtonNew.png`)
        : require(`@/Common/Icons/PlayButtonNew.png`)
    );
    const imageSoundOnSoundOffSource = computed(() =>
      volume.value > 0
        ? require(`@/Common/Icons/SoundOnButtonNew.png`)
        : require(`@/Common/Icons/SoundOffButtonNew.png`)
    );

    const muted = computed(() => volume.value === 0);

    const percentComplete = computed(() =>
      parseInt((currentSeconds.value / durationSeconds.value) * 100)
    );
    const progressStyle = computed(() => ({
      width: `${percentComplete.value}%`,
    }));
    const volumeTitle = computed(() => {
      return `Volume (${volume.value}%)`;
    });

    watch(playing, (newValue) => {
      newValue ? audioRef.value.play() : audioRef.value.pause();
    });
    watch(volume, (newValue) => {
      audioRef.value.volume = newValue / 100;
    });

    const convertTimeHHMMSS = (val) => {
      let hhmmss = new Date(val * 1000).toISOString().substr(11, 8);
      return hhmmss.indexOf("00:") === 0 ? hhmmss.substr(3) : hhmmss;
    };
    const load = () => {
      if (audioRef.value.readyState >= 2) {
        loaded.value = true;
        durationSeconds.value = parseInt(audioRef.value.duration);
        return (playing.value = props.autoPlay.value);
      }
      throw new Error("Failed to load sound file.");
    };
    const mute = () => {
      if (muted.value) {
        return (volume.value = previousVolume.value);
      }
      previousVolume.value = volume.value;
      volume.value = 0;
    };
    const seek = (e) => {
      if (!loaded.value) return;

      const bounds = e.target.getBoundingClientRect();
      const seekPos = (e.clientX - bounds.left) / bounds.width;

      audioRef.value.currentTime = parseInt(audioRef.value.duration * seekPos);
    };
    const stop = () => {
      playing.value = false;
      audioRef.value.currentTime = 0;
    };
    const update = () => {
      currentSeconds.value = parseInt(audioRef.value.currentTime);
      durationLeft.value = parseInt(
        audioRef.value.duration - audioRef.value.currentTime
      );
    };
    return {
      currentSeconds,
      durationSeconds,
      durationLeft,
      loaded,
      playing,
      previousVolume,
      showVolume,
      volume,
      muted,
      percentComplete,
      progressStyle,
      volumeTitle,
      audioRef,
      convertTimeHHMMSS,
      load,
      mute,
      seek,
      stop,
      update,
      imagePlayPauseSource,
      imageSoundOnSoundOffSource,
    };
  },
};
</script>
<style lang="scss" scoped src="@/Common/Styles/appAudio.scss"></style>
